import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useDataRefresh, DEFAULT_POLL_INTERVAL_MS } from '@/hooks/useDataRefresh';
import { Plus, Search, Scissors, Edit } from 'lucide-react';
import { CutMaterialFormDialog } from '@/components/Inventory/CutMaterialFormDialog';
import type { CutMaterial } from '@/types/legacy';

export default function CutMaterialPage() {
  const { userRole } = useAuth();
  const { toast } = useToast();
  const [materials, setMaterials] = useState<CutMaterial[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState<CutMaterial | null>(null);

  const fetchMaterials = async () => {
    try {
      const { data, error } = await api
        .from('cut_material')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setMaterials(data || []);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los materiales cortados',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMaterials();
  }, []);
  useDataRefresh(fetchMaterials, { pollIntervalMs: DEFAULT_POLL_INTERVAL_MS });

  const filteredMaterials = materials.filter(material =>
    material.material_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    material.material_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    material.batch_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEdit = (material: CutMaterial) => {
    setSelectedMaterial(material);
    setDialogOpen(true);
  };

  const handleNew = () => {
    setSelectedMaterial(null);
    setDialogOpen(true);
  };

  const canManage = userRole === 'admin' || userRole === 'supervisor';

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
      available: { label: 'Disponible', variant: 'default' },
      reserved: { label: 'Reservado', variant: 'secondary' },
      used: { label: 'Usado', variant: 'outline' },
    };
    const config = statusConfig[status] || statusConfig.available;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent">
              Material Cortado
            </h1>
            <p className="text-muted-foreground mt-2">
              Material preparado y listo para producción
            </p>
          </div>
          {canManage && (
            <Button onClick={handleNew} className="bg-gradient-magma hover:shadow-magma-hover">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Material
            </Button>
          )}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Buscar material cortado..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent">
                  {materials.length}
                </p>
              </div>
              <Scissors className="w-8 h-8 text-primary/50" />
            </div>
          </Card>

          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Disponible</p>
                <p className="text-3xl font-bold text-success">
                  {materials.filter(m => m.status === 'available').length}
                </p>
              </div>
            </div>
          </Card>

          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Asignado</p>
                <p className="text-3xl font-bold text-primary">
                  {materials.filter(m => m.assigned_to_work_order_id).length}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Materials Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : filteredMaterials.length === 0 ? (
          <Card className="glass-card p-12 text-center">
            <Scissors className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg text-muted-foreground">
              {searchTerm ? 'No se encontraron materiales' : 'No hay material cortado registrado'}
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredMaterials.map((material) => (
              <Card key={material.id} className="glass-card p-6 hover:shadow-magma transition-all">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Scissors className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{material.material_name}</h3>
                      {material.batch_number && (
                        <p className="text-sm text-muted-foreground">Lote: {material.batch_number}</p>
                      )}
                    </div>
                  </div>
                  {!material.is_active && (
                    <Badge variant="secondary" className="text-xs">
                      Inactivo
                    </Badge>
                  )}
                </div>

                {material.material_type && (
                  <div className="mb-3">
                    <Badge variant="outline">{material.material_type}</Badge>
                  </div>
                )}

                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Estado:</span>
                    {getStatusBadge(material.status)}
                  </div>

                  {material.dimensions && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Dimensiones:</span>
                      <span className="text-sm">{material.dimensions}</span>
                    </div>
                  )}

                  {material.thickness && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Grosor:</span>
                      <span className="text-sm">{material.thickness}</span>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Cantidad:</span>
                    <span className="text-sm font-medium">{material.quantity} {material.unit}</span>
                  </div>
                </div>

                {material.location && (
                  <div className="mb-3">
                    <p className="text-xs text-muted-foreground">📍 {material.location}</p>
                  </div>
                )}

                {material.assigned_to_work_order_id && (
                  <div className="mb-3">
                    <Badge className="text-xs">Asignado a OT</Badge>
                  </div>
                )}

                {canManage && (
                  <div className="mt-4 pt-4 border-t border-border/50">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(material)}
                      className="w-full"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Editar
                    </Button>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      <CutMaterialFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        cutMaterial={selectedMaterial}
        onSuccess={fetchMaterials}
      />
    </DashboardLayout>
  );
}
